import { useContext, type FC, type ReactNode } from "react";
import {
  FieldError as AriaFieldError,
  FieldErrorContext,
  type ValidationResult,
} from "react-aria-components";
import { fieldStyle } from "./style.js";

export interface FieldErrorProps {
  /**
   * Omit inside a react-aria field to render that field's own validation
   * messages; the render-prop form receives its validation state. Outside one
   * there is no validation state, so only plain content renders.
   */
  children?: ReactNode | ((validation: ValidationResult) => ReactNode);
  /**
   * Only for use outside a react-aria field, where the caller wires the
   * control's `aria-describedby` itself. Inside one, react-aria owns this id.
   */
  id?: string;
  className?: string;
}

/**
 * Error text for a form field, rendered below the control in place of its
 * {@link FieldDescription}.
 *
 * Inside a react-aria field (`Combobox`, `TextField`, `Select`, ...) the field
 * must be marked `isInvalid` for the message to show and to be picked up by the
 * control's `aria-describedby`. Outside one, pass an `id` and reference it from
 * the control's `aria-describedby`.
 */
export const FieldError: FC<FieldErrorProps> = (props) => {
  const { children, id, className } = props;
  const validation = useContext(FieldErrorContext);
  const errorClassName = fieldStyle().error({ className });

  if (validation !== null) {
    return (
      <AriaFieldError className={errorClassName} elementType="p">
        {children}
      </AriaFieldError>
    );
  }

  // Plain markup for controls that aren't built on react-aria (Input,
  // Textarea). Deliberately not react-aria's `Text slot="errorMessage"`: that
  // throws inside any react-aria context whose text slots don't include it —
  // a ListBoxItem, for instance, offers only "label" and "description".
  if (typeof children === "function") {
    return null;
  }

  return (
    <p className={errorClassName} id={id}>
      {children}
    </p>
  );
};
